import { useCallback, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { FilterConfig } from './FilterTrigger'
import type { FilterValues } from './FilterTriggerRow'

/**
 * URL-synced filter state for FilterTriggerRow (react-router v5): each config id becomes a
 * query param holding comma-separated option values, so filtered views are shareable and -
 * because changes push rather than replace - the back button walks filter history. Unknown
 * values are dropped on read, so a stale link never selects an option that no longer exists.
 * Kept apart from the components on purpose: Storybook stories run without a Router.
 */
export function useFilterUrlValues(configs: FilterConfig[]): [FilterValues, (next: FilterValues) => void] {
  const history = useHistory()
  const location = useLocation()

  const values = useMemo<FilterValues>(() => {
    const params = new URLSearchParams(location.search)
    return Object.fromEntries(
      configs.map((config) => {
        const known = new Set(config.options.map((option) => option.value))
        const raw = params.get(config.id)
        const parsed = raw ? raw.split(',').filter((value) => known.has(value)) : []
        return [config.id, parsed]
      })
    )
  }, [configs, location.search])

  const setValues = useCallback(
    (next: FilterValues) => {
      const params = new URLSearchParams(location.search)
      configs.forEach((config) => {
        const selected = next[config.id] ?? []
        if (selected.length === 0) params.delete(config.id)
        else params.set(config.id, selected.join(','))
      })
      history.push({ search: params.toString() ? `?${params.toString()}` : '' })
    },
    [configs, history, location.search]
  )

  return [values, setValues]
}
